import {
	type Accessor,
	createEffect,
	createMemo,
	createSignal,
	For,
	on,
	onCleanup,
	Show,
	untrack,
} from "solid-js";
import { Portal } from "solid-js/web";
import type {
	AudioListResponseItem,
	BoardClipSampledFrameResponse,
} from "@/client";
import "./VideoScrubber.css";
import { Text } from "@ui/typography/Text";
import { secondsToTime } from "@utils/time";
import { Handle } from "./components/Handle";

const MIN_GAP_SECONDS = 1;
/** Pointer movement past this (px) turns a strip press into a trim edit; smaller motion is a playhead seek. */
const SCRUBBER_TRIM_DRAG_THRESHOLD_PX = 6;
/** Must match `.video-scrubber__frame-wrap { height }` in CSS. */
const STRIP_FRAME_HEIGHT_REM = 3.5;

function stripRowHeightPx(): number {
	if (typeof document === "undefined") return 56;
	const remPx =
		parseFloat(
			globalThis.getComputedStyle(document.documentElement).fontSize,
		) || 16;
	return STRIP_FRAME_HEIGHT_REM * remPx;
}

function minGapSec(maxT: number): number {
	return Math.min(MIN_GAP_SECONDS, maxT);
}

/** Frame indices to show in the strip: first, last, evenly spaced between. */
function evenlySpacedIndices(
	totalFrames: number,
	visibleCount: number,
): number[] {
	const n = Math.max(1, Math.floor(visibleCount));
	const t = totalFrames;
	if (t <= 0) return [];
	const nShow = Math.min(n, t);
	if (nShow === 1) return [0];
	if (nShow >= t) return Array.from({ length: t }, (_, i) => i);
	const out: number[] = [];
	for (let k = 0; k < nShow; k++) {
		out.push(Math.round((k * (t - 1)) / (nShow - 1)));
	}
	return out;
}

function indexClosestTime(times: number[], t: number): number {
	if (times.length === 0) return 0;
	let best = 0;
	let bestDiff = Infinity;
	for (let i = 0; i < times.length; i++) {
		const d = Math.abs(times[i] - t);
		if (d < bestDiff) {
			bestDiff = d;
			best = i;
		}
	}
	return best;
}

type Props = {
	/** Video URL (same source as the main player / waveform). */
	url: string;
	/**
	 * Filmstrip thumbnails from `getBoardClip` (e.g. `?num_frames=50` → `sampled_frames`).
	 * Each entry needs a `signed_url`; timestamps should be `timestamp_seconds` when available.
	 */
	sampledFrames?: BoardClipSampledFrameResponse[] | null;
	/** Trim start time in seconds (`0` … `durationSeconds`). Snapped to `stepSeconds` when dragging. @default 0 */
	rangeStart?: number;
	/**
	 * Trim end time in seconds (`0` … `durationSeconds`).
	 * Omit for “through end of clip” until the user drags the end handle.
	 */
	rangeEnd?: number;
	/**
	 * Called when the trim range changes — during handle / selection drags and when the gesture ends
	 * (also on pointer cancel). Values are scrubber-local seconds (0 … duration).
	 */
	onRangeChange?: (range: { start: number; end: number }) => void;
	/**
	 * When set, waveform player is shown under the thumbnail strip (API audio URL).
	 * If omitted, playback can still use the same video URL when `durationSeconds`
	 * or loaded metadata supplies a duration (same URL; waveform may stay placeholder).
	 */
	audio?: AudioListResponseItem;
	/** Clip duration in seconds (e.g. from clip). Enables audio strip from the video URL without a separate audio asset. */
	durationSeconds?: number;
	/** Paired preview is playing; playhead is full opacity while true, slightly dimmed when false. */
	playing?: Accessor<boolean>;
	/** Source timeline seconds (0 … duration); drives the playhead on the strip. */
	playbackPositionSeconds?: Accessor<number>;
	/**
	 * Click/tap on the strip (without dragging the trim) seeks paired playback to this time (0 … duration).
	 * Press-and-drag past a small movement threshold still edits the trim (viewport or selection body).
	 */
	onPlaybackSeek?: (timelineSeconds: number) => void;
	/**
	 * Trim handles, strip drags, and keyboard nudges snap to multiples of this many seconds.
	 * Arrow keys move one step; Shift+Arrow moves 10× this value.
	 * @default 0.5
	 */
	stepSeconds?: number;
};

/**
 * Trim UI with a filmstrip built from `sampledFrames` (server-provided stills), plus optional waveform from `url`.
 */
export const VideoScrubber = (props: Props) => {
	const stepSeconds = () => {
		const s = props.stepSeconds ?? 0.5;
		return s > 0 && Number.isFinite(s) ? s : 0.5;
	};

	function snapSec(v: number, maxT: number): number {
		const step = stepSeconds();
		const snapped = Math.round(v / step) * step;
		const rounded = step >= 1 ? snapped : Number.parseFloat(snapped.toFixed(4));
		return Math.max(0, Math.min(maxT, rounded));
	}

	const [frames, setFrames] = createSignal<string[]>([]);
	/** Video time in seconds for each entry in {@link frames} (same order). */
	const [frameTimes, setFrameTimes] = createSignal<number[]>([]);
	/** Native video dimensions (for thumbnail cell aspect ratio). */
	const [videoPixelSize, setVideoPixelSize] = createSignal<{
		w: number;
		h: number;
	} | null>(null);
	const [trackInnerWidth, setTrackInnerWidth] = createSignal(0);
	const [hoverTip, setHoverTip] = createSignal<{
		x: number;
		y: number;
		src: string;
		timeSec: number;
	} | null>(null);
	const [phase, setPhase] = createSignal<
		"idle" | "loading" | "ready" | "error"
	>("idle");
	const [errorMessage, setErrorMessage] = createSignal<string | null>(null);

	const [rangeStart, setRangeStart] = createSignal(props.rangeStart ?? 0);
	/** `null` = use full clip length (`timelineMax`). */
	const [rangeEnd, setRangeEnd] = createSignal<number>(
		props.rangeEnd ?? props.durationSeconds,
	);
	const [loadedDuration, setLoadedDuration] = createSignal<number | undefined>(
		undefined,
	);

	const timelineMax = createMemo(() => {
		const d = props.durationSeconds ?? loadedDuration();
		return d != null && Number.isFinite(d) && d > 0 ? d : 0;
	});

	const visibleStripIndices = createMemo(() => {
		const all = frames().length;
		const w = trackInnerWidth();
		if (all === 0 || w <= 0) return [];
		const ar = videoPixelSize();
		const vwOverVh = ar ? ar.w / ar.h : 16 / 9;
		/** Fixed row height × video aspect ⇒ cell width; same as CSS `height` + `aspect-ratio`. */
		const cellW = stripRowHeightPx() * vwOverVh;
		const nFit = Math.max(1, Math.floor(w / cellW));
		return evenlySpacedIndices(all, nFit);
	});

	const visibleStripItems = createMemo(() => {
		const f = frames();
		const times = frameTimes();
		const indices = visibleStripIndices();
		return indices.map((idx) => ({
			idx,
			src: f[idx] ?? "",
			timeSec: times[idx] ?? 0,
		}));
	});

	const playheadLeftPct = createMemo(() => {
		const acc = props.playbackPositionSeconds;
		if (acc == null) return null;
		const maxT = timelineMax();
		if (maxT <= 0) return null;
		const t = Math.max(0, Math.min(maxT, acc()));
		return (t / maxT) * 100;
	});

	const playheadOpacity = createMemo(() => {
		if (props.playing == null) return 1;
		return props.playing() ? 1 : 0.45;
	});

	function effectiveEndSec(): number {
		const maxT = timelineMax();
		if (maxT <= 0) return 0;
		const e = rangeEnd();
		if (e === null) return maxT;
		return Math.max(0, Math.min(maxT, e));
	}

	function effectiveStartSec(): number {
		const maxT = timelineMax();
		if (maxT <= 0) return 0;
		const gap = minGapSec(maxT);
		const endE = effectiveEndSec();
		const s = snapSec(rangeStart(), maxT);
		return Math.min(s, Math.max(0, endE - gap));
	}

	createEffect(
		on(timelineMax, (maxT) => {
			if (maxT <= 0) return;
			untrack(() => {
				const gap = minGapSec(maxT);
				let s = snapSec(rangeStart(), maxT);
				const endStored = rangeEnd();
				let e = endStored === null ? maxT : snapSec(endStored ?? maxT, maxT);
				if (e - s < gap) {
					e = Math.min(maxT, s + gap);
					s = Math.max(0, e - gap);
				}
				setRangeStart(s);
				if (endStored === null) {
					return;
				}
				setRangeEnd(e);
			});
		}),
	);

	/** Parent-controlled trim (e.g. keep preview playback aligned with handles). */
	createEffect(() => {
		const maxT = timelineMax();
		const rs = props.rangeStart;
		const re = props.rangeEnd;
		if (maxT <= 0) return;
		untrack(() => {
			if (rs !== undefined) {
				setRangeStart(snapSec(rs, maxT));
			}
			if (re !== undefined) {
				setRangeEnd(snapSec(re, maxT));
			}
		});
	});

	let trackRef: HTMLDivElement | undefined;
	let scrubberRootEl: HTMLDivElement | undefined;
	let startHandleEl: HTMLButtonElement | undefined;
	let endHandleEl: HTMLButtonElement | undefined;
	const [viewportEl, setViewportEl] = createSignal<
		HTMLDivElement | undefined
	>();

	createEffect(() => {
		const el = viewportEl();
		if (!el) return;
		const measure = () => {
			// Timeline width for thumbnail count & layout: full viewport content box (not shrink-wrapped track).
			const style = getComputedStyle(el);
			const pad =
				(parseFloat(style.paddingLeft) || 0) +
				(parseFloat(style.paddingRight) || 0);
			setTrackInnerWidth(Math.max(0, el.getBoundingClientRect().width - pad));
		};
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		queueMicrotask(measure);
		onCleanup(() => ro.disconnect());
	});

	/** Viewport X for a timeline second (for keyboard tooltip placement). */
	function clientXForTimelineSec(tSec: number): number | null {
		if (!trackRef) return null;
		const maxT = timelineMax();
		if (maxT <= 0) return null;
		const rect = trackRef.getBoundingClientRect();
		if (rect.width <= 0) return null;
		const r = Math.max(0, Math.min(1, snapSec(tSec, maxT) / maxT));
		return rect.left + r * rect.width;
	}

	/** Frame preview tooltip at pointer X (trim drag / keyboard). Not used for idle hover. */
	function setTrimTooltipFromClientX(clientX: number) {
		const maxT = timelineMax();
		const all = frames();
		const times = frameTimes();
		if (
			all.length === 0 ||
			times.length !== all.length ||
			maxT <= 0 ||
			!trackRef ||
			!scrubberRootEl
		) {
			return;
		}
		const rect = trackRef.getBoundingClientRect();
		if (rect.width <= 0) return;
		const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
		/** Scrub-local seconds (matches trim UI timeline 0 … maxT). */
		const tScrub = ratio * maxT;

		let idx = 0;
		if (times.length > 0) {
			const tMinData = Math.min(...times);
			const tMaxData = Math.max(...times);
			const dataSpan = tMaxData - tMinData;
			if (dataSpan > 1e-12) {
				/** Map across the span of sampled timestamps (often absolute on-video, not 0…maxT). */
				const tSeek = tMinData + ratio * dataSpan;
				idx = indexClosestTime(times, tSeek);
			}
		}

		const remPx =
			typeof document !== "undefined"
				? parseFloat(
						globalThis.getComputedStyle(document.documentElement).fontSize,
					) || 16
				: 16;
		const anchorY = scrubberRootEl.getBoundingClientRect().top - 0.5 * remPx;
		setHoverTip({
			x: clientX,
			y: anchorY,
			src: all[idx] ?? "",
			timeSec: tScrub,
		});
	}

	function notifyRangeChange() {
		props.onRangeChange?.({
			start: effectiveStartSec(),
			end: effectiveEndSec(),
		});
	}

	function nudgeStartBy(deltaSec: number) {
		const maxT = timelineMax();
		if (maxT <= 0) return;
		const gap = minGapSec(maxT);
		const endE = effectiveEndSec();
		setRangeStart((s) => {
			const ns = snapSec(s + deltaSec, maxT);
			return Math.max(0, Math.min(ns, endE - gap));
		});
	}

	function nudgeEndBy(deltaSec: number) {
		const maxT = timelineMax();
		if (maxT <= 0) return;
		const gap = minGapSec(maxT);
		const startE = effectiveStartSec();
		setRangeEnd((e) => {
			const ne = snapSec(e + deltaSec, maxT);
			return Math.max(startE + gap, Math.min(maxT, ne));
		});
	}

	function onScrubberKeyDown(e: KeyboardEvent) {
		if (frames().length === 0) return;
		const target = e.target as Node | null;
		const onStart = startHandleEl != null && target === startHandleEl;
		const onEnd = endHandleEl != null && target === endHandleEl;
		if (!onStart && !onEnd) return;

		if (e.key === "ArrowRight") {
			e.preventDefault();
			endHandleEl?.focus();
		} else if (e.key === "ArrowLeft") {
			e.preventDefault();
			startHandleEl?.focus();
		} else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
			e.preventDefault();
			const base = stepSeconds();
			const deltaMag = e.shiftKey ? base * 10 : base;
			const delta = e.key === "ArrowUp" ? deltaMag : -deltaMag;
			if (onStart) {
				nudgeStartBy(delta);
			} else {
				nudgeEndBy(delta);
			}
			notifyRangeChange();
			const tShow = onStart ? effectiveStartSec() : effectiveEndSec();
			const x = clientXForTimelineSec(tShow);
			if (x != null) {
				setTrimTooltipFromClientX(x);
			}
		}
	}

	function snapSecFromClientX(clientX: number): number | null {
		if (!trackRef) return null;
		const maxT = timelineMax();
		if (maxT <= 0) return null;
		const rect = trackRef.getBoundingClientRect();
		if (rect.width <= 0) return null;
		const rawSec = ((clientX - rect.left) / rect.width) * maxT;
		return snapSec(rawSec, maxT);
	}

	/** Set trim range from two timeline positions (pointer down / up); order can be reversed. */
	function applyRangeFromDragEnds(secDown: number, secUp: number) {
		const maxT = timelineMax();
		if (maxT <= 0) return;
		const gap = minGapSec(maxT);
		let lo = snapSec(Math.min(secDown, secUp), maxT);
		let hi = snapSec(Math.max(secDown, secUp), maxT);
		if (hi - lo < gap) {
			hi = Math.min(maxT, lo + gap);
			lo = Math.max(0, hi - gap);
		}
		setRangeStart(lo);
		setRangeEnd(hi);
	}

	function moveRangeByDelta(
		startAtDragStart: number,
		endAtDragStart: number,
		deltaSec: number,
	) {
		const maxT = timelineMax();
		if (maxT <= 0) return;
		const width = Math.max(minGapSec(maxT), endAtDragStart - startAtDragStart);
		const maxStart = Math.max(0, maxT - width);
		const nextStart = Math.max(
			0,
			Math.min(maxStart, snapSec(startAtDragStart + deltaSec, maxT)),
		);
		setRangeStart(nextStart);
		setRangeEnd(nextStart + width);
	}

	function beginSelectionDrag(e: PointerEvent) {
		if (e.button !== 0) return;
		const downSec = snapSecFromClientX(e.clientX);
		const maxT = timelineMax();
		if (downSec === null || maxT <= 0) return;

		e.preventDefault();
		e.stopPropagation();

		const startAtDragStart = effectiveStartSec();
		const endAtDragStart = effectiveEndSec();
		const target = e.currentTarget as HTMLElement;
		const pid = e.pointerId;
		target.setPointerCapture(pid);
		setTrimTooltipFromClientX(e.clientX);

		const downX = e.clientX;
		const downY = e.clientY;
		let selectionDragActive = false;

		const onMove = (ev: PointerEvent) => {
			if (ev.pointerId !== pid) return;
			const dx = ev.clientX - downX;
			const dy = ev.clientY - downY;
			if (
				!selectionDragActive &&
				dx * dx + dy * dy >= SCRUBBER_TRIM_DRAG_THRESHOLD_PX ** 2
			) {
				selectionDragActive = true;
			}
			if (!selectionDragActive) return;
			const currentSec = snapSecFromClientX(ev.clientX);
			if (currentSec === null) return;
			moveRangeByDelta(startAtDragStart, endAtDragStart, currentSec - downSec);
			setTrimTooltipFromClientX(ev.clientX);
			notifyRangeChange();
		};

		const finish = (ev: PointerEvent) => {
			if (ev.pointerId !== pid) return;
			if (target.hasPointerCapture(pid)) {
				target.releasePointerCapture(pid);
			}
			document.removeEventListener("pointermove", onMove);
			document.removeEventListener("pointerup", finish);
			document.removeEventListener("pointercancel", finish);
			if (selectionDragActive) {
				notifyRangeChange();
			} else {
				const seekSec = snapSecFromClientX(ev.clientX) ?? downSec;
				props.onPlaybackSeek?.(seekSec);
			}
			setHoverTip(null);
		};

		document.addEventListener("pointermove", onMove);
		document.addEventListener("pointerup", finish);
		document.addEventListener("pointercancel", finish);
	}

	function beginViewportRangeDrag(e: PointerEvent) {
		if (e.button !== 0) return;
		if (frames().length === 0) return;
		const downSec = snapSecFromClientX(e.clientX);
		if (downSec === null) return;
		e.preventDefault();
		const el = e.currentTarget as HTMLElement;
		const pid = e.pointerId;
		el.setPointerCapture(pid);

		const downX = e.clientX;
		const downY = e.clientY;
		let rangeDragActive = false;

		const onMove = (ev: PointerEvent) => {
			if (ev.pointerId !== pid) return;
			const dx = ev.clientX - downX;
			const dy = ev.clientY - downY;
			if (
				!rangeDragActive &&
				dx * dx + dy * dy >= SCRUBBER_TRIM_DRAG_THRESHOLD_PX ** 2
			) {
				rangeDragActive = true;
			}
			if (!rangeDragActive) return;
			const s = snapSecFromClientX(ev.clientX);
			if (s === null) return;
			applyRangeFromDragEnds(downSec, s);
			setTrimTooltipFromClientX(ev.clientX);
		};

		const finish = (ev: PointerEvent) => {
			if (ev.pointerId !== pid) return;
			if (el.hasPointerCapture(pid)) {
				el.releasePointerCapture(pid);
			}
			document.removeEventListener("pointermove", onMove);
			document.removeEventListener("pointerup", finish);
			document.removeEventListener("pointercancel", finish);
			if (rangeDragActive) {
				const s = snapSecFromClientX(ev.clientX);
				if (s !== null) {
					applyRangeFromDragEnds(downSec, s);
				}
				notifyRangeChange();
			} else {
				const seekSec = snapSecFromClientX(ev.clientX) ?? downSec;
				props.onPlaybackSeek?.(seekSec);
			}
			setHoverTip(null);
		};

		document.addEventListener("pointermove", onMove);
		document.addEventListener("pointerup", finish);
		document.addEventListener("pointercancel", finish);
	}

	function updateRangeFromPointer(clientX: number, kind: "start" | "end") {
		if (!trackRef) return;
		const maxT = timelineMax();
		if (maxT <= 0) return;
		const rect = trackRef.getBoundingClientRect();
		if (rect.width <= 0) return;
		const rawSec = ((clientX - rect.left) / rect.width) * maxT;
		const t = snapSec(rawSec, maxT);
		const gap = minGapSec(maxT);
		const storedEnd = rangeEnd();
		const endE =
			storedEnd === null ? maxT : Math.max(0, Math.min(maxT, storedEnd));
		const startS = rangeStart();
		const startSnapped = snapSec(startS, maxT);
		if (kind === "start") {
			setRangeStart(snapSec(Math.min(t, Math.max(0, endE - gap)), maxT));
		} else {
			setRangeEnd(
				snapSec(Math.min(maxT, Math.max(t, startSnapped + gap)), maxT),
			);
		}
	}

	function beginHandleDrag(kind: "start" | "end", e: PointerEvent) {
		e.preventDefault();
		e.stopPropagation();
		const target = e.currentTarget as HTMLElement;
		target.setPointerCapture(e.pointerId);
		setTrimTooltipFromClientX(e.clientX);

		const onMove = (ev: PointerEvent) => {
			updateRangeFromPointer(ev.clientX, kind);
			setTrimTooltipFromClientX(ev.clientX);
			notifyRangeChange();
		};

		const onUp = (ev: PointerEvent) => {
			if (target.hasPointerCapture(ev.pointerId)) {
				target.releasePointerCapture(ev.pointerId);
			}
			document.removeEventListener("pointermove", onMove);
			document.removeEventListener("pointerup", onUp);
			document.removeEventListener("pointercancel", onUp);
			notifyRangeChange();
			setHoverTip(null);
		};

		document.addEventListener("pointermove", onMove);
		document.addEventListener("pointerup", onUp);
		document.addEventListener("pointercancel", onUp);
	}

	createEffect(() => {
		const url = props.url;
		const sampledFiltered =
			props.sampledFrames?.filter(
				(f): f is BoardClipSampledFrameResponse & { signed_url: string } =>
					Boolean(f.signed_url && f.signed_url.length > 0),
			) ?? [];

		if (!url) {
			setFrames([]);
			setFrameTimes([]);
			setVideoPixelSize(null);
			setPhase("idle");
			setLoadedDuration(undefined);
			setErrorMessage(null);
			return;
		}

		if (sampledFiltered.length === 0) {
			setFrames([]);
			setFrameTimes([]);
			setVideoPixelSize(null);
			setLoadedDuration(undefined);
			setPhase("error");
			setErrorMessage(
				"No frame thumbnails: request the clip with a num_frames query (sampled_frames).",
			);
			return;
		}

		let cancelled = false;
		const durProp = props.durationSeconds ?? 0;
		const times = sampledFiltered.map((f, i) => {
			if (f.timestamp_seconds != null && Number.isFinite(f.timestamp_seconds)) {
				return f.timestamp_seconds as number;
			}
			if (durProp > 0) {
				if (sampledFiltered.length === 1) {
					return durProp * 0.5;
				}
				return Math.min(
					(i * (durProp - 1e-4)) / (sampledFiltered.length - 1),
					durProp,
				);
			}
			return i;
		});
		const frameUrls = sampledFiltered.map((f) => f.signed_url);

		setPhase("loading");
		setErrorMessage(null);
		setFrames([]);
		setFrameTimes([]);
		setVideoPixelSize(null);

		const img = new Image();
		img.onload = () => {
			if (cancelled) return;
			if (img.naturalWidth >= 2 && img.naturalHeight >= 2) {
				setVideoPixelSize({
					w: img.naturalWidth,
					h: img.naturalHeight,
				});
			} else {
				setVideoPixelSize(null);
			}
			setFrames([...frameUrls]);
			setFrameTimes([...times]);
			const ld = durProp > 0 ? durProp : Math.max(0, ...times, Number.EPSILON);
			setLoadedDuration(ld);
			setPhase("ready");
		};
		img.onerror = () => {
			if (cancelled) return;
			setVideoPixelSize(null);
			setFrames([...frameUrls]);
			setFrameTimes([...times]);
			const ld = durProp > 0 ? durProp : Math.max(0, ...times, Number.EPSILON);
			setLoadedDuration(ld);
			setPhase("ready");
		};
		img.src = frameUrls[0];

		onCleanup(() => {
			cancelled = true;
		});
	});

	return (
		<div>
			<Show when={frames().length > 0}>
				<div class="video-scrubber-info">
					<Text
						as="h3"
						family="mono"
						size="small"
						weight="bold"
						transform="uppercase"
					>
						Trim clip
					</Text>
					<Text family="mono" size="small">
						<div class="range">
							{secondsToTime(rangeStart(), "MM:SS")} -{" "}
							{secondsToTime(rangeEnd(), "MM:SS")}
						</div>
					</Text>
					<Text family="mono" size="small" style={{ "text-align": "right" }}>
						{secondsToTime(props.durationSeconds, "MM:SS")}
					</Text>
				</div>
			</Show>
			<div
				class="video-scrubber"
				role="toolbar"
				aria-label="Video scrubber"
				ref={(el) => {
					scrubberRootEl = el ?? undefined;
				}}
				onKeyDown={onScrubberKeyDown}
			>
				<Show when={phase() === "loading"}>
					<div class="video-scrubber__status">Loading scrubber…</div>
				</Show>
				<Show when={phase() === "error" && errorMessage()}>
					<div class="video-scrubber__status">{errorMessage()}</div>
				</Show>
				<Show when={frames().length > 0}>
					<div
						class="video-scrubber__viewport"
						ref={(el) => setViewportEl(el ?? undefined)}
						onPointerDown={beginViewportRangeDrag}
					>
						<div class="video-scrubber__track" ref={trackRef}>
							<div class="video-scrubber__track-core">
								<div class="video-scrubber__filmstrip">
									<ul
										class="video-scrubber__strip"
										style={{
											...(() => {
												const ar = videoPixelSize();
												return ar
													? {
															"--video-ar-w": String(ar.w),
															"--video-ar-h": String(ar.h),
														}
													: {
															"--video-ar-w": "16",
															"--video-ar-h": "9",
														};
											})(),
										}}
									>
										<For each={visibleStripItems()}>
											{(item) => (
												<li class="video-scrubber__frame-wrap">
													<img
														class="video-scrubber__frame"
														src={item.src}
														alt={`Frame at ${secondsToTime(item.timeSec, "MM:SS")}`}
														loading="lazy"
														draggable={false}
													/>
												</li>
											)}
										</For>
									</ul>
								</div>

								<div class="video-scrubber__range-layer">
									<div
										class="video-scrubber__range-dim video-scrubber__range-dim--left"
										style={{
											width: `${
												timelineMax() > 0
													? (effectiveStartSec() / timelineMax()) * 100
													: 0
											}%`,
										}}
									/>
									<div
										class="video-scrubber__range-dim video-scrubber__range-dim--right"
										style={{
											width: `${
												timelineMax() > 0
													? (1 - effectiveEndSec() / timelineMax()) * 100
													: 0
											}%`,
										}}
									/>
									<div
										class="video-scrubber__selection"
										style={{
											left: `${
												timelineMax() > 0
													? (effectiveStartSec() / timelineMax()) * 100
													: 0
											}%`,
											width: `${
												timelineMax() > 0
													? (
															(effectiveEndSec() - effectiveStartSec()) /
																timelineMax()
														) * 100
													: 0
											}%`,
										}}
									>
										<div
											class="video-scrubber__selection-fill"
											onPointerDown={beginSelectionDrag}
										/>
										<Handle
											type="start"
											ref={(el) => {
												startHandleEl = el ?? undefined;
											}}
											onPointerDown={(e) => beginHandleDrag("start", e)}
										/>
										<Handle
											type="end"
											ref={(el) => {
												endHandleEl = el ?? undefined;
											}}
											onPointerDown={(e) => beginHandleDrag("end", e)}
										/>
									</div>
									<Show when={playheadLeftPct() != null}>
										<div
											class="video-scrubber__playhead"
											style={{
												left: `${playheadLeftPct()}%`,
												opacity: playheadOpacity(),
											}}
											aria-hidden="true"
										/>
									</Show>
								</div>
							</div>
						</div>
					</div>
				</Show>
				<Show when={hoverTip()}>
					{(getTip) => (
						<Portal>
							<div
								class="video-scrubber__tooltip"
								style={{
									left: `${getTip().x}px`,
									top: `${getTip().y}px`,
								}}
							>
								<img
									class="video-scrubber__tooltip-img"
									src={getTip().src}
									alt=""
								/>
								<div class="video-scrubber__tooltip-time">
									{secondsToTime(getTip().timeSec, "MM:SS")}
								</div>
							</div>
						</Portal>
					)}
				</Show>
			</div>
		</div>
	);
};
